import React, {useMemo, useCallback, useEffect} from 'react';
import DefaultPage from '../../layout/common/DefaultPage';
import {Button, Input, Row, Col, Table, Empty, Spin} from 'antd';
import {SearchOutlined, ReloadOutlined} from '@ant-design/icons';
import {ScenarioExecutionGroupDetailDTO, ScenarioExecutionOrderBy} from 'hemwb-api';
import {AsyncActionPhase} from '../../../store/asyncUtilsTypes';
import styles from './ScenarioExecutionQueue.module.scss';
import {fullName} from '../../../store/user/utils';
import {Link} from 'react-router-dom';
import {getUrlRoute, MRAppRoutes} from '../../router/routes';
import {tid} from '../../../testUtils';
import ScenarioInstanceList from './ScenarioInstanceList';
import {scenarioExecutionSelector, scenarioExecutionGroupLoadList} from '../../../store/scenarioExecution';
import ExecutionStatus from './ExecutionStatus';
import {useSelector} from '../../../store/rootReducer';
import {useDispatch} from 'react-redux';
import {useExecutionStatusUpdate} from '../../../websocket/useExecutionStatusUpdate';
import {useStoredGridState, scenarioExecutionQueueGridId, Order} from '../../../store/grid';
import type {ColumnsType, TablePaginationConfig} from 'antd/es/table';
import type {SorterResult} from 'antd/es/table/interface';
import type {GridState} from '../../../store/grid/types';

// Standardized date format constant
const TO_LOCALE_DATE_STRING_OPTIONS = {
  year: 'numeric' as const,
  month: 'short' as const,
  day: 'numeric' as const,
  hour: 'numeric' as const,
  minute: '2-digit' as const,
  hour12: true as const,
};

const RestrictedAccessText = () => <span className={styles.restricted}>Restricted access</span>;

// Map table column keys to API orderBy enum values
const getOrderByValue = (field: string): ScenarioExecutionOrderBy | null => {
  const fieldMapping: {[key: string]: ScenarioExecutionOrderBy} = {
    name: ScenarioExecutionOrderBy.ScenarioName,
    fileName: ScenarioExecutionOrderBy.Filename,
    executedBy: ScenarioExecutionOrderBy.ExecutedBy,
    createdOn: ScenarioExecutionOrderBy.CreatedOn,
    status: ScenarioExecutionOrderBy.Status,
    modelName: ScenarioExecutionOrderBy.ModelName,
  };
  return fieldMapping[field] || null;
};

const ModelDetailLink = ({model}: ScenarioExecutionGroupDetailDTO) => {
  const displayName = model?.name && model.name.length > 60 ? `${model.name.substring(0, 60)}...` : model?.name;
  return model ? (
    <Link
      style={{textDecoration: 'underline', color: '#000'}}
      to={getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid: model.uuid})}
    >
      {displayName}
    </Link>
  ) : (
    <RestrictedAccessText />
  );
};

const ScenarioExecutionQueue: React.FC = () => {
  useExecutionStatusUpdate();
  const dispatch = useDispatch();
  const gridState = useStoredGridState(scenarioExecutionQueueGridId);

  const data = useSelector((state) => scenarioExecutionSelector(state).groupByList.value);
  const loadingState = useSelector((state) => scenarioExecutionSelector(state).groupByList.state);
  const isLoading = loadingState === AsyncActionPhase.START;

  // Loader callback that coordinates with GridState changes
  const loader = useCallback(
    (gState: GridState) => {
      return scenarioExecutionGroupLoadList(dispatch, {
        pageSize: gState.rowsPerPage,
        pageIndex: gState.page * gState.rowsPerPage,
        orderBy: (gState.orderBy as any as ScenarioExecutionOrderBy) || ScenarioExecutionOrderBy.CreatedOn,
        orderDesc: gState.order === Order.DESC,
      });
    },
    [dispatch],
  );

  // Trigger loader when gridState changes
  useEffect(() => {
    loader(gridState);
  }, [gridState.page, gridState.rowsPerPage, gridState.orderBy, gridState.order, loader]);

  // Filter data based on search text (client-side filtering on current page only)
  const filteredData = useMemo(() => {
    if (!data?.list) return [];
    if (!gridState.search.trim()) return data.list;

    const lcSearch = gridState.search.toLowerCase();
    return data.list.filter(
      (item) =>
        (item.name || '').toLowerCase().includes(lcSearch) || (item.model?.name || '').toLowerCase().includes(lcSearch),
    );
  }, [data?.list, gridState.search]);

  const columns: ColumnsType<ScenarioExecutionGroupDetailDTO> = useMemo(
    () => [
      {
        title: 'Instance Name',
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        width: '20%',
        render: (text: string) => text || '-',
      },
      {
        title: 'File Name',
        dataIndex: 'fileName',
        key: 'fileName',
        sorter: true,
        width: '22%',
        render: (_, record: ScenarioExecutionGroupDetailDTO) => {
          const filename = record.executionDetails?.[0]?.filename;
          return filename || <RestrictedAccessText />;
        },
      },
      {
        title: 'Model Name',
        dataIndex: 'modelName',
        key: 'modelName',
        sorter: true,
        width: '20%',
        render: (text: string, record: ScenarioExecutionGroupDetailDTO) => <ModelDetailLink model={record.model} />,
      },
      {
        title: 'Executed by',
        dataIndex: 'executedBy',
        key: 'executedBy',
        sorter: true,
        width: '15%',
        render: (_, record: ScenarioExecutionGroupDetailDTO) => (record.executedBy ? fullName(record.executedBy) : '-'),
      },
      {
        title: 'Executed on',
        dataIndex: 'createdOn',
        key: 'createdOn',
        sorter: true,
        width: '15%',
        render: (_, record: ScenarioExecutionGroupDetailDTO) =>
          record.createdOn ? record.createdOn.toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS) : '-',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        sorter: true,
        width: '8%',
        render: (_, record: ScenarioExecutionGroupDetailDTO) => <ExecutionStatus status={record.status} />,
      },
    ],
    [],
  );

  // Handle search change
  const handleSearchChange = useCallback(
    (searchValue: string) => {
      gridState.setSearch(searchValue);
    },
    [gridState],
  );

  // Handle refresh button click - AJAX call to reload data
  const handleRefresh = useCallback(() => {
    loader(gridState);
  }, [gridState, loader]);

  const handleTableChange = (
    pag: TablePaginationConfig,
    _: any,
    sorter: SorterResult<ScenarioExecutionGroupDetailDTO> | SorterResult<ScenarioExecutionGroupDetailDTO>[],
  ) => {
    const newPageSize = pag.pageSize || 50;
    const newCurrent = pag.current || 1;
    const pageIndex = newCurrent - 1; // Convert 1-based to 0-based

    gridState.setPage(pageIndex);
    gridState.setRowsPerPage(newPageSize);

    // Handle sorting changes
    if (!Array.isArray(sorter) && sorter.field) {
      const orderByValue = getOrderByValue(sorter.field as string);
      if (orderByValue) {
        gridState.setOrderBy(orderByValue);
        gridState.setOrder((sorter.order === 'descend' ? Order.DESC : Order.ASC) as any);
      }
    } else if (!Array.isArray(sorter) && !sorter.field) {
      // Sort has been cleared
      gridState.setOrderBy(ScenarioExecutionOrderBy.CreatedOn as any);
      gridState.setOrder(Order.DESC as any);
    }
  };

  return (
    <DefaultPage
      style={{backgroundColor: '#FFF', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '10px'}}
    >
      {/* Header Section */}
      <div style={{backgroundColor: '#FFF', padding: '24px', color: '#000'}}>
        <Row align="middle" gutter={[16, 0]}>
          <Col flex="auto">
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <div>
                <h1 style={{margin: 0, fontSize: '24px', fontWeight: 'normal', color: '#00857c', lineHeight: 1.3}}>
                  Execution Queue
                </h1>
                <p style={{margin: '4px 0 0 0', fontSize: '14px', lineHeight: 1.3}}>
                  We found <b>{data?.total || 0} Instances</b>
                </p>
              </div>
            </div>
          </Col>
          <Col flex="auto" xs={28} sm={8}>
            <Input
              style={{border: '2px solid #00857C', borderRadius: '24px'}}
              placeholder="Type Instance Name"
              prefix={<SearchOutlined style={{color: '#8c8c8c'}} />}
              value={gridState.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              allowClear
              {...tid('input', 'search-executions')}
            />
          </Col>
          <Col xs={28} sm={2}>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={isLoading}
              style={{width: '100%', fontWeight: 'bold', borderRadius: '0px'}}
              {...tid('btn', 'refresh-list')}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </div>

      {/* Table Section */}
      <div style={{flex: 1, overflow: 'auto', padding: '24px'}}>
        <Spin spinning={isLoading} size="large" tip="Loading...">
          <Table<ScenarioExecutionGroupDetailDTO>
            columns={columns}
            dataSource={filteredData}
            loading={isLoading}
            rowKey={(record) => record.groupId || record.id}
            pagination={{
              current: gridState.page + 1,
              pageSize: gridState.rowsPerPage,
              total: data?.total || 0,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['50', '100', '200'],
              position: ['bottomRight'],
            }}
            onChange={handleTableChange}
            locale={{emptyText: 'No executions were found'}}
            size="middle"
            style={{width: '100%'}}
            scroll={{x: 'max-content'}}
            expandable={{
              expandedRowRender: (record) => {
                const hasDetails = !!(record.executionDetails && record.executionDetails.length > 0);
                return hasDetails ? (
                  <ScenarioInstanceList scenarioExecutionInstance={record.executionDetails} />
                ) : (
                  <Empty description="No execution details available" style={{margin: '20px'}} />
                );
              },
              expandIcon: ({expanded, onExpand, record}) => (
                <Button
                  type="text"
                  size="small"
                  onClick={(e) => {
                    onExpand(record, e);
                  }}
                >
                  {expanded ? 'v' : '>'}
                </Button>
              ),
            }}
            {...tid('executions-table')}
          />
        </Spin>
      </div>
    </DefaultPage>
  );
};

export default ScenarioExecutionQueue;
